import { Component, OnInit, ViewChild, NgZone, ViewChildren, AfterViewInit, Inject, ElementRef } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { catchError } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { RegistrationService } from 'src/app/registration/shared/registration.service';
import { Employment } from 'src/app/shared/models/employment.model';
import { DateTimeService } from 'src/app/shared/date-time-convertor/date-time.service';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { DriverApiService } from 'src/app/shared/services/driver-api.service';
import { DatePipe } from '@angular/common';
import { Location } from '@angular/common';
import dayjs from 'dayjs';

import { ToastService } from 'src/app/shared/toast/toast.service';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { Driver } from 'src/app/shared/models/driver.model';
import { of } from 'rxjs';
import { StorageService } from 'src/app/shared/storage/storage.service';
import { CANADA_PROVINCES, CERTN_COUNTRIES, USA_PROVINCES } from 'src/app/shared/models/provinces';
import { compareEmploymentByToDateDesc } from 'src/app/shared/utils/employment-sort.util';
import {
  CROSS_STATE_OVERLAP_WARNING_MESSAGE,
  EMPLOYMENT_STATE_EMPLOYED,
  hasOppositeStateDateOverlap
} from 'src/app/shared/utils/employment-overlap.util';
import { ROUTE_EMPLOYMENT_HISTORY } from 'src/app/shared/routes';

const REQUIRED_FIELD_MESSAGE = 'You must enter a value';
const VALID_REFERENCE_PHONE_NUMBER_FORMAT_MESSAGE = 'Must be ###-###-####';
const EARLIEST_EMPLOYMENT_YEAR = 1900;

export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/YYYY'
  },
  display: {
    dateInput: 'MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};
const InvalidEmailAddress = 'Invalid email address';

@Component({
    selector: 'app-employment-history-form-employed',
    templateUrl: './employment-history-form-employed.component.html',
    styleUrls: ['./employment-history-form-employed.component.scss'],
    providers: [
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
        DatePipe
    ],
    standalone: false,
    host: {
        '[class.show-nav]': 'showNavBar'
    }
})
export class EmploymentHistoryFormEmployedComponent implements OnInit, AfterViewInit {
  employmentList = [];
  isFirstRecord: boolean;
  form: UntypedFormGroup;
  errorMessage: string;
  overlapWarningMessage: string;
  currentDate = new Date();
  lastDate: Date;
  isGreaterThan10years: boolean = false;
  editFormTitle: string = 'Employment';
  minFromDate;
  maxToDate: Date;
  isContentReady: boolean = false;
  employmentObj: any;
  isModifyNewCurrentAddress: boolean;
  driver: Driver;
  currentNewEmployment;
  isGoogleAddressLookupEnabled: boolean = true; // Default value for toggle
  isGoogleLookupReinitPending: boolean = false; // Track reinitialization
  showNavBar: boolean = true; // Controls bottom navigation visibility
  @ViewChild('empAddressText', { static: false }) empAddressText: any;
  @ViewChildren('toDatePicker') toDateList;
  @ViewChildren('fromDatePicker') fromDateList;
  @ViewChild('employer', { static: false }) employerInput: any;
  @ViewChild('datePickerSection', { static: false }) datePickerSection: ElementRef;
  private subs = new SubSink();
  licenseClassNonON = ['Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5'];
  licenseClassON = ['Class A', 'Class B', 'Class C', 'Class D', 'Class E', 'Class F '];
  allLicenseClasses = [
    'Class 1',
    'Class 2',
    'Class 3',
    'Class 4',
    'Class 5',
    'Class A',
    'Class B',
    'Class C',
    'Class D',
    'Class E',
    'Class F '
  ];
  radiusCanada = [
    { value: 'provincial', label: 'Provincial' },
    { value: 'acrossCanada', label: 'Canada wide' },
    { value: 'canadaAndUS', label: 'Canada & US' }
  ];
  radiusUs = [
    { value: 'intrastate', label: 'Intrastate' },
    { value: 'acrossUS', label: 'Across US' },
    { value: 'USAndCanada', label: 'US & Canada' }
  ];
  provinceLicenseClass = [];
  assignedRadiusValues = [];
  isOntario = false;
  positions = [
    { value: 'companyDriver', displayName: 'Company Driver' },
    { value: 'ownerOperator', displayName: 'Owner Operator' },
    { value: 'other', displayName: 'Other' }
  ];

  // Month and year options for dropdowns
  months = [
    { value: 0, name: 'January' },
    { value: 1, name: 'February' },
    { value: 2, name: 'March' },
    { value: 3, name: 'April' },
    { value: 4, name: 'May' },
    { value: 5, name: 'June' },
    { value: 6, name: 'July' },
    { value: 7, name: 'August' },
    { value: 8, name: 'September' },
    { value: 9, name: 'October' },
    { value: 10, name: 'November' },
    { value: 11, name: 'December' }
  ];

  // Generate years from 30 years ago to current year
  years: number[] = (() => {
    const currentYear = new Date().getFullYear();
    const startYear = EARLIEST_EMPLOYMENT_YEAR;
    const yearsArray: number[] = [];
    for (let year = currentYear; year >= startYear; year--) {
      yearsArray.push(year);
    }
    return yearsArray;
  })();

  // Cached properties for dropdowns (computed once and cached)
  availableFromYears: number[] = [];
  availableFromMonths: any[] = [];
  availableToYears: number[] = [];
  availableToMonths: any[] = [];
  equipmentTypes: string[] = [];
  allCountries = CERTN_COUNTRIES;
  countries = [...this.allCountries];
  selectedCountry: string;
  provinces = [];
  canadaProvinces = CANADA_PROVINCES;
  usaProvinces = USA_PROVINCES;
  employerCountry: string;

  constructor(
    private activatedRoute: ActivatedRoute,
    private readonly registrationService: RegistrationService,
    private readonly location: Location,
    private readonly router: Router,
    private readonly fb: UntypedFormBuilder,
    private readonly toastService: ToastService,
    private readonly zone: NgZone,
    private readonly storageService: StorageService,
    @Inject('environmentData') public environment: any
  ) {
    this.errorMessage = undefined;
    this.form = this.createForm();
    this.initializeDates();
  }

  private initializeDates() {
    // Set current date to end of month
    this.currentDate = new Date();
    this.currentDate.setMonth(this.currentDate.getMonth() + 1, 0);
    
    // Calculate date 10 years ago
    this.lastDate = new Date(this.currentDate);
    this.lastDate.setFullYear(this.lastDate.getFullYear() - 10);
    
    this.maxToDate = new Date(this.currentDate);
  }

  ngOnInit(): void {
    this.driver = this.registrationService.getDriver();
    this.currentNewEmployment = this.storageService.find('currentEmploymentState');
    this.loadExperience();
    this.selectedCountry = this.form.get('country').value;
    this.setProvinces();
    this.onPositionChanged();
    this.provinceLicenseClass = this.allLicenseClasses;
  }

  ngAfterViewInit(): void {
    if (this.isGoogleAddressLookupEnabled) {
      this.loadGoogleAutoComplete();
    }

    setTimeout(() => {
      this.employerInput?.nativeElement?.focus();
    }, 0);
  }

  ngAfterViewChecked(): void {
    if (this.isGoogleLookupReinitPending && this.empAddressText?.nativeElement) {
      this.isGoogleLookupReinitPending = false;
      this.getPlaceAutocomplete(this.empAddressText);
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onPositionChanged() {
    this.position.valueChanges.subscribe(value => {
      this.updateValidators();
    });
  }

  updateValidators() {
    const positionValue = this.form.get('position')?.value;
    const otherPositionControl = this.form.get('otherPosition');
    const licenseTypeControl = this.form.get('licenseType');
    const assignedRadiusControl = this.form.get('assignedRadius');
    const referenceContactNameControl = this.form.get('referenceContactName');
    const referenceContactEmailControl = this.form.get('referenceEmail');
    const referenceContactPhoneNumberControl = this.form.get('referenceContactPhoneNumber');

    if (positionValue === 'other') {
      otherPositionControl.setValidators([Validators.required]);
      referenceContactNameControl.setValidators([Validators.pattern("^[A-Za-z-'\\s]+$")]);
      referenceContactEmailControl.setValidators([
        Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\\.[A-Za-z0-9]+)((\\.[A-Za-z]{2,}){0,1})\\s*$')
      ]);
      referenceContactPhoneNumberControl.setValidators([Validators.minLength(12)]);
    } else {
      otherPositionControl.clearValidators();
      otherPositionControl.setValue('');
      referenceContactNameControl.clearValidators();
      referenceContactEmailControl.clearValidators();
      referenceContactPhoneNumberControl.clearValidators();
    }

    otherPositionControl.updateValueAndValidity();
    if (positionValue === 'companyDriver' || positionValue === 'ownerOperator') {
      this.form.setValidators(this.atLeastOneCheckboxCheckedValidator);
      assignedRadiusControl.setValidators([Validators.required]);
      licenseTypeControl.setValidators([Validators.required]);

      referenceContactNameControl.setValidators([Validators.required, Validators.pattern("^[A-Za-z-'\\s]+$")]);

      referenceContactEmailControl.setValidators([
        Validators.required,
        Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\\.[A-Za-z0-9]+)((\\.[A-Za-z]{2,}){0,1})\\s*$')
      ]);

      referenceContactPhoneNumberControl.setValidators([Validators.required, Validators.minLength(12)]);
    } else {
      assignedRadiusControl.clearValidators();
      licenseTypeControl.clearValidators();
      assignedRadiusControl.setValue('');
      licenseTypeControl.setValue('');

      const currentValidators = this.form.validator
        ? [this.form.validator].filter(v => v !== this.atLeastOneCheckboxCheckedValidator)
        : [];
      this.form.setValidators(currentValidators);
    }

    // Update fields with new validators
    assignedRadiusControl.updateValueAndValidity();
    licenseTypeControl.updateValueAndValidity();
    referenceContactNameControl.updateValueAndValidity();
    referenceContactEmailControl.updateValueAndValidity();
    referenceContactPhoneNumberControl.updateValueAndValidity();
  }

  onCertnCountrySearch(event: any): void {
    const searchText = event?.target?.value?.trim();
    if (searchText) {
      this.countries = this.allCountries.filter(country =>
        country.key.toLowerCase().includes(searchText.toLowerCase())
      );
    } else {
      this.countries = [...this.allCountries];
    }
  }

  onAddNewEmployment(isMissingIndex?: number, gapPrevFromDate?: string, gapNextToDate?: string): void {
    
    // IMPORTANT: Set minFromDate and maxToDate BEFORE creating the form
    // so the year/month filters work correctly from the start
    if (isMissingIndex !== undefined && isMissingIndex !== null) {
      const nextEmp = this.employmentList[isMissingIndex + 1];
      const prevEmp = this.employmentList[isMissingIndex - 1];
      const prevFrom = gapPrevFromDate ?? prevEmp?.fromDate;
      const nextTo = gapNextToDate ?? nextEmp?.toDate;
      
      if (nextTo && prevFrom) {
        this.minFromDate = dayjs(nextTo).toDate();
        this.maxToDate = dayjs(prevFrom).toDate();
      } else {
        // Fallback if dates are missing
        this.minFromDate = null;
        this.maxToDate = new Date(this.currentDate);
      }
    } else {
      // Reset for non-gap scenarios
      this.minFromDate = null;
      this.maxToDate = new Date(this.currentDate);
    }
    
    this.form = this.createForm();
    this.editFormTitle = 'Employment';

    // Check if this is for the first record or a new current employment
    if (this.isFirstRecord || this.isModifyNewCurrentAddress) {
      this.editFormTitle = 'Where do you currently work?';
      this.toDate.setValue(this.currentDate);
      this.form.get('isCurrentEmployment').setValue(true);
      
      // Set default month/year for current employment
      const currentMonth = dayjs(this.currentDate).month();
      const currentYear = dayjs(this.currentDate).year();
      this.form.patchValue({
        toMonth: currentMonth,
        toYear: currentYear
      });
      // Clear validators for TO date fields for current employment
      this.form.get('toMonth')?.clearValidators();
      this.form.get('toYear')?.clearValidators();
      this.form.get('toMonth')?.updateValueAndValidity();
      this.form.get('toYear')?.updateValueAndValidity();
    } else {
      // This is for a previous employment
      this.editFormTitle = 'Where did you work before?';

      // Check if there's a current employment record
      const hasCurrentEmployment = this.employmentList.some(emp => emp && emp.isCurrentEmployment);

      if (!hasCurrentEmployment && (isMissingIndex === undefined || isMissingIndex === null)) {
        // Block adding previous employment without a current one
        this.toastService.showError('You need to add your current employment first.');
        this.location.back();
        return;
      }

      // Set appropriate dates - prefill based on the last entry
      const lastEmployment = this.employmentList[this.employmentList.length - 1];
      
      // Get the month before the last employment's FROM date
      const toDateDayjs = dayjs(lastEmployment.fromDate).subtract(1, 'month').endOf('month');
      
      this.form.get('toDate').setValue(toDateDayjs.toDate());
      this.form.get('isCurrentEmployment').setValue(false);
      
      // Pre-fill TO month/year based on last employment's FROM date (month before)
      this.form.patchValue({
        toMonth: toDateDayjs.month(),
        toYear: toDateDayjs.year()
      });
      
    }

    if (isMissingIndex !== undefined && isMissingIndex !== null) {
      const nextEmp = this.employmentList[isMissingIndex + 1];
      const prevEmp = this.employmentList[isMissingIndex - 1];
      
      const prevFrom = gapPrevFromDate ?? prevEmp?.fromDate;
      const nextTo = gapNextToDate ?? nextEmp?.toDate;

      if (prevFrom && nextTo) {
        const toDateForGap = dayjs(prevFrom).subtract(1, 'month').endOf('month');
        const fromDateForGap = dayjs(nextTo).add(1, 'month').startOf('month');
        
        this.form.patchValue({
          toMonth: toDateForGap.month(),
          toYear: toDateForGap.year(),
          fromMonth: fromDateForGap.month(),
          fromYear: fromDateForGap.year()
        });
        
      }
      
    }
    
    // Update cached dropdown options after form is initialized
    this.updateDropdownOptions();

    if (this.isModifyNewCurrentAddress) {
      // When user adds new current record, they should not be able to choose toDate month of previous record
      if (this.employmentList && this.employmentList.length > 0) {
        const baseDate = new Date(
          this.currentNewEmployment?.isCurrentEmployment
            ? this.employmentList[0]?.fromDate
            : this.employmentList[0]?.toDate
        );
        baseDate.setMonth(baseDate.getMonth() + 1);
        baseDate.setDate(1);
        this.minFromDate = baseDate;
      }
    }
    this.loadGoogleAutoComplete();
  }

  onUpdateEmploymentPressed($event): void {
    if ($event) {
      this.maxToDate = new Date(this.currentDate);
      this.form = this.createForm();
      this.populateForm($event);

      if ($event?.isCurrentEmployment) {
        this.editFormTitle = 'Where do you currently work?';
      } else {
        this.editFormTitle = 'Where did you work before?';
      }
      this.employmentObj = this.employmentList.find(e => e.id === $event.id);
      this.loadGoogleAutoComplete();
    }
  }

  onCancel(): void {
    if (this.isModifyNewCurrentAddress) {
      this.registrationService.updateEmploymentHistoryStore([]);
      this.router.navigateByUrl(`/${ROUTE_EMPLOYMENT_HISTORY}`);
      return;
    }
    this.location.back();
  }

  onLog() {
  }

  goBack(): void {
    this.location.back();
  }

  onSubmit(form: UntypedFormGroup): void {
    this.isContentReady = false;
    if (!form.valid) {
      return;
    }
    
    let model = null;
    model = this.prepareSaveModel();

    const modelWithId = { ...model };

    if (model) {
      const excludeIds: string[] = [];
      if (modelWithId.id) {
        excludeIds.push(modelWithId.id);
      }
      if (
        this.currentNewEmployment?.isCurrentEmployment &&
        this.isModifyNewCurrentAddress &&
        model.isCurrentEmployment &&
        this.currentNewEmployment?.id
      ) {
        excludeIds.push(this.currentNewEmployment.id);
      }
    }

    if (model && model.id) {
      let id = model.id;
      delete model.id;
      if (model.isCurrentEmployment) {
        delete model.toDate;
        delete model.reasonForLeaving;
      }
      if (model?.employerAddress) {
        const countryMatch = model?.employerAddress?.match(/, ([A-Za-z\s]+)$/);
        model['employerCountry'] = countryMatch ? countryMatch[1].trim() : null;
      }
      Object.keys(model).forEach(key => {
        if (model[key] === null || model[key] === '') {
          delete model[key];
        }
      });
      this.subs.add(
        this.registrationService.updateEmployment(model, id).subscribe(
          () => {
            this.errorMessage = undefined;
            const updatedEmp = this.employmentList.findIndex(emp => emp.id === modelWithId.id);
            this.employmentList[updatedEmp] = modelWithId;
            this.registrationService.updateEmploymentHistoryStore([...this.employmentList]);
            this.form.reset();
            this.onCancel();
          },
          (error: any) => {
            this.toastService.showError(error?.message);
            this.isContentReady = true;
            this.form.reset();
            this.onCancel();
          }
        )
      );
    } else if (model) {
      
      // Only take the "update current" path if we're explicitly in "modify current address" mode
      // AND the model we're adding is for a current employment
      if (this.currentNewEmployment?.isCurrentEmployment && this.isModifyNewCurrentAddress && model.isCurrentEmployment) {
        const previousCurrentPayload = {
          ...this.currentNewEmployment,
          isCurrentEmployment: false,
          toDate: dayjs(this.currentDate).endOf('month').format('YYYY-MM-DD')
        };

        const id = previousCurrentPayload.id;
        delete previousCurrentPayload.id;
        delete previousCurrentPayload.modifiedDate;
        delete previousCurrentPayload.creationDate;
        delete previousCurrentPayload.version;
        delete previousCurrentPayload.reasonForLeaving;

        this.updateExistingAndCreateNewEmployment(previousCurrentPayload, id, model);
      } else {
        this.onEmploymentAdded(model);
      }
    } else {
      this.onCancel();
    }
  }

  private updateExistingAndCreateNewEmployment(model: any, id: string, newCurrentEmployment?: Employment): void {
    if (model?.employerAddress) {
      const countryMatch = model?.employerAddress?.match(/, ([A-Za-z\s]+)$/);
      model['employerCountry'] = countryMatch ? countryMatch[1].trim() : null;
    }
    Object.keys(model).forEach(key => {
      if (model[key] === null || model[key] === '') {
        delete model[key];
      }
    });
    this.isContentReady = false;
    this.registrationService
      .updateEmployment(model, id)
      .pipe(
        catchError((error: any) => {
          this.isContentReady = true;
          this.toastService.showError(error?.message);
          return of(false);
        })
      )
      .subscribe(res => {
        if (res) {
          this.errorMessage = undefined;
          if (this.currentNewEmployment) {
            const modelToSave = { ...model, id: id };
            const updatedEmp = this.employmentList.findIndex(emp => emp.id === id);
            this.employmentList[updatedEmp] = modelToSave;
            this.registrationService.updateEmploymentHistoryStore([...this.employmentList]);
            this.storageService.store('currentEmploymentState', null);
            this.onEmploymentAdded(newCurrentEmployment);
          }
        }
      });
  }

  get employerAddress() {
    return this.form.get('employerAddress');
  }
  get employer() {
    return this.form.get('employer');
  }
  get fromDate() {
    return this.form.get('fromDate');
  }
  get toDate() {
    return this.form.get('toDate');
  }
  get position() {
    return this.form.get('position');
  }
  get otherPosition() {
    return this.form.get('otherPosition');
  }
  get licenseType() {
    return this.form.get('licenseType');
  }
  get assignedRadius() {
    return this.form.get('assignedRadius');
  }
  get referenceContactName() {
    return this.form.get('referenceContactName');
  }
  get referenceContactPhoneNumber() {
    return this.form.get('referenceContactPhoneNumber');
  }
  get referenceEmail() {
    return this.form.get('referenceEmail');
  }
  get isCurrentEmployment() {
    return this.form.get('isCurrentEmployment');
  }
  get equipmentTypeDescription() {
    return this.form.get('equipmentTypeDescription');
  }
  getEmployerAddressErrorMessage() {
    return this.employerAddress.errors.required ? REQUIRED_FIELD_MESSAGE : '';
  }
  getEmployerNameErrorMessage() {
    return this.employer.errors.required ? REQUIRED_FIELD_MESSAGE : '';
  }

  getFromDateErrorMessage() {
    return this.fromDate.errors.required ? REQUIRED_FIELD_MESSAGE : '';
  }

  getToDateErrorMessage() {
    return this.toDate.errors.required ? REQUIRED_FIELD_MESSAGE : '';
  }

  getPositionErrorMessage() {
    const errors = this.otherPosition.errors;
    if (errors?.required) {
      return REQUIRED_FIELD_MESSAGE;
    }
    return '';
  }

  getReferenceContactNameErrorMessage() {
    return this.referenceContactName.errors.required ? REQUIRED_FIELD_MESSAGE : '';
  }

  getreferenceContactPhoneNumberErrorMessage() {
    const errors = this.referenceContactPhoneNumber.errors;
    return errors.required ? REQUIRED_FIELD_MESSAGE : errors ? VALID_REFERENCE_PHONE_NUMBER_FORMAT_MESSAGE : '';
  }

  getReferenceEmailErrorMessage() {
    const errors = this.referenceEmail.errors;
    return errors.required ? REQUIRED_FIELD_MESSAGE : errors.pattern ? InvalidEmailAddress : '';
  }

  getRequiredFieldMessage(control: string): string {
    const errors = this.form.get(control)?.errors;
    return errors?.required ? REQUIRED_FIELD_MESSAGE : '';
  }

  onDatePickerClick(datepicker: any) {
    datepicker.open();
  }

  private setLicense(province: string): void {
    // this.isOntario = false;
    // this.provinceLicenseClass = this.licenseClassNonON;
    // if (province === 'ON') {
    //   this.isOntario = true;
    //   this.provinceLicenseClass = this.licenseClassON;
    // }
    this.provinceLicenseClass = this.allLicenseClasses;
  }

  private setAssignedRadius(country: string) {
    if (country === 'Canada') {
      this.assignedRadiusValues = this.radiusCanada;
    } else if (country === 'USA') {
      this.assignedRadiusValues = this.radiusUs;
    } else {
      this.assignedRadiusValues = [];
    }
  }

  private createForm(): UntypedFormGroup {
    const form = this.fb.group({
      employer: ['', Validators.required],
      fromDate: ['', [Validators.required, this.checkFromDateValidation.bind(this)]],
      toDate: ['', [Validators.required, this.checkToDateValidation.bind(this)]],
      // New month/year controls for FROM date
      fromMonth: ['', Validators.required],
      fromYear: ['', Validators.required],
      // New month/year controls for TO date
      toMonth: ['', Validators.required],
      toYear: ['', Validators.required],
      position: ['', [Validators.required, this.positionValidator]],
      otherPosition: [''],
      licenseType: [''],
      assignedRadius: [''],
      dumpTruck: [false],
      straightTruck: [false],
      flatbed: [false],
      dryVan: [false],
      reefer: [false],
      tanker: [false],
      other: [false],
      equipmentTypeDescription: [''],
      id: [''],
      referenceContactName: [''],
      referenceContactPhoneNumber: [''],
      referenceEmail: [''],
      reasonForLeaving: [''],
      employerAddress: ['', Validators.required],
      streetAddress: [''],
      city: [''],
      province: [''],
      zipCode: [''],
      country: [''],
      isCurrentEmployment: [''],
      employerCountry: ['']
    });

    // Keep date fields in sync as user selects month/year
    form.get('fromMonth')?.valueChanges.subscribe((month) => {
      // If month is cleared/null, also clear the date
      if (month === null || month === '') {
        form.patchValue({ fromDate: null }, { emitEvent: false });
      }
      this.updateDateFieldsFromMonthYear();
      this.updateDropdownOptions(); // Recalculate available options
      this.errorMessage = undefined; // Clear error when dates change
      this.checkOverlapWarning();
    });
    form.get('fromYear')?.valueChanges.subscribe((year) => {
      // If year is cleared/null, also clear the date
      if (year === null || year === '') {
        form.patchValue({ fromDate: null }, { emitEvent: false });
      }
      this.updateDateFieldsFromMonthYear();
      this.updateDropdownOptions(); // Recalculate available options
      this.errorMessage = undefined; // Clear error when dates change
      this.checkOverlapWarning();
    });
    form.get('toMonth')?.valueChanges.subscribe((month) => {
      // If month is cleared/null, also clear the date
      if (month === null || month === '') {
        form.patchValue({ toDate: null }, { emitEvent: false });
      }
      this.updateDateFieldsFromMonthYear();
      this.updateDropdownOptions(); // Recalculate available options
      this.errorMessage = undefined; // Clear error when dates change
      this.checkOverlapWarning();
    });
    form.get('toYear')?.valueChanges.subscribe((year) => {
      // If year is cleared/null, also clear the date
      if (year === null || year === '') {
        form.patchValue({ toDate: null }, { emitEvent: false });
      }
      this.updateDateFieldsFromMonthYear();
      this.updateDropdownOptions(); // Recalculate available options
      this.errorMessage = undefined; // Clear error when dates change
      this.checkOverlapWarning();
    });

    // Handle equipmentTypeDescription validation when 'other' checkbox changes
    form.get('other')?.valueChanges.subscribe((isOtherChecked) => {
      const equipmentDescControl = form.get('equipmentTypeDescription');
      if (isOtherChecked) {
        equipmentDescControl.setValidators([Validators.required, Validators.pattern('^[^<>]{1,500}$')]);
      } else {
        equipmentDescControl.clearValidators();
        equipmentDescControl.setValue('');
      }
      equipmentDescControl.updateValueAndValidity();
    });

    return form;
  }

  private positionValidator(control: AbstractControl): ValidationErrors | null {
    const value = control?.value;
    if (!value || value === '-') {
      return { required: true };
    }
    return null;
  }

  atLeastOneCheckboxCheckedValidator(control: AbstractControl): ValidationErrors | null {
    const dumpTruck = control.get('dumpTruck')?.value;
    const straightTruck = control.get('straightTruck')?.value;
    const flatbed = control.get('flatbed')?.value;
    const dryvan = control.get('dryVan')?.value;
    const reefer = control.get('reefer')?.value;
    const tank = control.get('tanker')?.value;
    const other = control.get('other')?.value;

    // Check if at least one checkbox is true
    const atLeastOneChecked = dumpTruck || straightTruck || flatbed || dryvan || reefer || tank || other;

    return atLeastOneChecked ? null : { atLeastOneCheckboxRequired: true };
  }

  private checkOverlapWarning(): void {
    const fromDate = this.form.get('fromDate')?.value;
    const toDate = this.form.get('toDate')?.value;
    if (!fromDate) {
      this.overlapWarningMessage = undefined;
      return;
    }
    const candidate = {
      fromDate: fromDate,
      toDate: toDate || null,
      id: this.form.get('id')?.value || null,
      employmentState: EMPLOYMENT_STATE_EMPLOYED
    } as any;
    const excludeIds = candidate.id ? [candidate.id] : [];
    const hasOverlap = hasOppositeStateDateOverlap(
      candidate,
      EMPLOYMENT_STATE_EMPLOYED,
      this.employmentList,
      this.currentDate,
      excludeIds
    );
    this.overlapWarningMessage = hasOverlap ? CROSS_STATE_OVERLAP_WARNING_MESSAGE : undefined;
  }

  private checkFromDateValidation(control: AbstractControl): Promise<any> {
    if (control && control?.parent) {
      let fromDate = control?.value;
      const toDate = control?.parent?.get('toDate').value || this.currentDate;
      if (fromDate && toDate && dayjs(fromDate) >= dayjs(toDate)) {
        return Promise.resolve({
          maxDateError: true
        });
      }
    }
    return null;
  }

  private checkToDateValidation(control: AbstractControl): Promise<any> {
    if (control && control?.parent) {
      let toDate = control?.value;
      const fromDate = control?.parent?.get('fromDate').value;
      if (fromDate && toDate && dayjs(toDate) <= dayjs(fromDate)) {
        return Promise.resolve({
          minDateError: true
        });
      }
      if (toDate) {
        const t = dayjs(toDate);
        const n = dayjs();
        if (t.year() > n.year() || (t.year() === n.year() && t.month() > n.month())) {
          return Promise.resolve({
            futureToDateError: true
          });
        }
      }
    }
    return null;
  }

  private prepareSaveModel(): Employment {
    if (!this.form.value) {
      return;
    }
  
  
    const formModel = this.form.value;
    const trimString = (value: any) => (typeof value === 'string' ? value.trim() : value);
    formModel.employer = trimString(formModel.employer);
    formModel.position = trimString(formModel.position);
    formModel.otherPosition = trimString(formModel.otherPosition);
    formModel.licenseType = trimString(formModel.licenseType);
    formModel.assignedRadius = trimString(formModel.assignedRadius);
    formModel.id = trimString(formModel.id);
    formModel.referenceContactName = trimString(formModel.referenceContactName);
    formModel.referenceContactPhoneNumber = trimString(formModel.referenceContactPhoneNumber);
    formModel.referenceEmail = trimString(formModel.referenceEmail);
    formModel.reasonForLeaving = trimString(formModel.reasonForLeaving);
    formModel.streetAddress = trimString(formModel.streetAddress);
    formModel.city = trimString(formModel.city);
    formModel.province = trimString(formModel.province);
    formModel.zipCode = trimString(formModel.zipCode);
    formModel.country = trimString(formModel.country);
  
    if (!this.isGoogleAddressLookupEnabled) {
      const street = formModel.streetAddress || '';
      const city = formModel.city || '';
      const province = formModel.province || '';
      const zip = formModel.zipCode || '';
      const country = formModel.country || '';
  
      formModel.employerAddress = `${street}, ${city}, ${province} ${zip}, ${country}`.trim();
      this.employerCountry = country;
    } else {
      formModel.employerAddress = trimString(formModel.employerAddress);
      this.employerCountry = trimString(this.employerCountry);
    }
  
    // Build equipment types array
    const equipmentTypes: string[] = [];
    if (formModel.dumpTruck) equipmentTypes.push('dumpTruck');
    if (formModel.straightTruck) equipmentTypes.push('straightTruck');
    if (formModel.flatbed) equipmentTypes.push('flatbed');
    if (formModel.dryVan) equipmentTypes.push('dryVan');
    if (formModel.reefer) equipmentTypes.push('reefer');
    if (formModel.tanker) equipmentTypes.push('tanker');
    if (formModel.other) equipmentTypes.push('other');
  
    // Construct model
    
    const formattedFromDate = formModel.fromDate ? (dayjs(formModel.fromDate).format('YYYY-MM-DD') as string) : '';
    const formattedToDate = formModel.toDate ? (dayjs(formModel.toDate).format('YYYY-MM-DD') as string) : '';
    
    
    const model: Employment = {
      employer: formModel.employer,
      fromDate: formattedFromDate,
      toDate: formattedToDate,
      position: formModel.otherPosition || formModel.position,
      licenseType: formModel.licenseType || '',
      assignedRadius: formModel.assignedRadius || '',
      id: formModel.id || '',
      referenceContactName: formModel.referenceContactName || '',
      referenceContactPhoneNumber: formModel.referenceContactPhoneNumber
        ? this.getPhoneNumberWithoutDash(formModel.referenceContactPhoneNumber)
        : '',
      referenceEmail: formModel.referenceEmail || '',
      employmentState: EMPLOYMENT_STATE_EMPLOYED,
      reasonForLeaving: formModel.reasonForLeaving || '',
      employerAddress: formModel.employerAddress || '',
      isCurrentEmployment: formModel.isCurrentEmployment || '',
      equipmentTypes: equipmentTypes.length ? equipmentTypes : undefined,
      equipmentTypeDescription: formModel.other ? formModel.equipmentTypeDescription?.trim() : undefined,
      employerCountry: this.employerCountry || ''
    };
  
    if (this.isFirstRecord || model?.isCurrentEmployment) {
      model.isCurrentEmployment = true;
      delete model.toDate;
    }
  
    // Remove null or empty string values
    Object.keys(model).forEach(key => {
      if (model[key] === null || model[key] === '') {
        delete model[key];
      }
    });
  
    delete model.otherPosition;
  
    if (model.position !== 'ownerOperator' && model.position !== 'companyDriver') {
      delete model.licenseType;
      delete model.assignedRadius;
      delete model.equipmentTypes;
      delete model.equipmentTypeDescription;
    }
  
    
    return model as Employment;
  }
  

  getCountryCodeByKey(key: string): string {
    const country = CERTN_COUNTRIES.find(country => country.key === key);
    return country ? country.value : '';
  }

  private populateForm(employment: Employment): void {
    
    const provinceMatch = employment.employerAddress.match(/, ([A-Z]{2}) \w+ \d{5}|\d{3}|\d{4},/);
    let provinceValue = provinceMatch ? provinceMatch[1] : null;
    const countryMatch = employment.employerAddress.match(/, ([A-Za-z\s]+)$/);
    const country = countryMatch ? countryMatch[1].trim() : null;
    this.employerCountry = country;
    if (!provinceValue) {
      const fullAddress = employment.employerAddress.split(',');
      if (fullAddress.length > 2) {
        const provinceAndZip = fullAddress[fullAddress.length - 2].trim();
        const getProvince = provinceAndZip.split(' ');
        if (getProvince.length > 0) {
          provinceValue = getProvince[0];
        }
      }
    }
    this.setAssignedRadius(country);
    const phoneNumberWithDashes = employment?.referenceContactPhoneNumber
      ? employment.referenceContactPhoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
      : '';
    if (employment.equipmentTypes && Array.isArray(employment.equipmentTypes)) {
      employment.equipmentTypes.forEach((type: string) => {
        const checkboxControl = this.form.get(type);
        if (checkboxControl) {
          checkboxControl.setValue(true);
        }
      });
    }
    if (employment.equipmentTypeDescription) {
      this.form.get('equipmentTypeDescription').setValue(employment.equipmentTypeDescription);
    }
    
    // Populate month/year dropdowns from the date values FIRST
    this.populateMonthYearFromDate(employment);
    
    // Explicitly clear the date fields - they will be recalculated from month/year
    // Don't set fromDate/toDate directly - they will be calculated from month/year
    const patchData: any = {
      employer: employment.employer,
      fromDate: null,  // Clear old date - will be recalculated
      toDate: null,    // Clear old date - will be recalculated
      assignedRadius: employment.assignedRadius,
      licenseType: employment.licenseType,
      id: employment.id,
      referenceContactName: employment.referenceContactName || '',
      referenceContactPhoneNumber: phoneNumberWithDashes || '',
      referenceEmail: employment.referenceEmail || '',
      reasonForLeaving: employment.reasonForLeaving || '',
      employerAddress: employment.employerAddress || '',
      isCurrentEmployment: employment?.isCurrentEmployment,
      country: country,
      province: this.provinces,
      employerCountry: country
    };
    if (employment.position) {
      if (employment.position === 'companyDriver' || employment.position === 'ownerOperator') {
        patchData.position = employment.position;
        patchData.otherPosition = '';
      } else {
        patchData.position = 'other';
        patchData.otherPosition = employment.position;
      }
    }
    
    this.form.patchValue(patchData);
    
    
    // Trigger date calculation from the month/year values
    this.updateDateFieldsFromMonthYear();
    
    // Update dropdown options based on the current form state
    this.updateDropdownOptions();
    
    // Ensure TO month/year fields are enabled when editing (they might be disabled when adding new)
    // Only enable if it's a previous employment being edited (not current employment)
    if (employment.id && !employment.isCurrentEmployment) {
      this.form.get('toMonth')?.enable();
      this.form.get('toYear')?.enable();
    }
    
    this.updateValidators();
  }

  private getPhoneNumberWithoutDash(phoneNumber): string {
    return phoneNumber.replace(/-/g, '') as string;
  }

  private scrollToDatePicker(): void {
    if (this.datePickerSection) {
      this.datePickerSection.nativeElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }

  private onEmploymentAdded(model: Employment) {
    // TODO: Remove this logic when API ignores these fields.
    model.id = undefined;
    if (this.isModifyNewCurrentAddress) {
      model.isCurrentEmployment = true;
      delete model.toDate;
    }
    this.isContentReady = false;
    this.subs.add(
      this.registrationService
        .saveEmployment(model)
        .pipe(
          catchError((error: any) => {
            this.isContentReady = true;
            this.toastService.showError(error?.message);
            return of(false);
          })
        )
        .subscribe(response => {
          if (response?.data) {
            const empRecords = [...this.employmentList];
            empRecords.push(response?.data);
            const recordsToSave = [...empRecords];
            this.registrationService.updateEmploymentHistoryStore(recordsToSave);
          }
          this.onCancel();
        })
    );
  }

  private loadExperience(): void {
    this.isContentReady = false;
    this.registrationService.getEmploymentHistory().subscribe(response => {
      this.employmentList = [...response];
      if (this.employmentList) {
        if (this.employmentList?.length) {
          this.employmentList.sort((a, b) => compareEmploymentByToDateDesc(a, b, this.currentDate));

          // Check if there's already a current employment
          const hasCurrentEmployment = this.employmentList.some(emp => emp && emp.isCurrentEmployment);
          this.isFirstRecord = !hasCurrentEmployment;
          this.checkMissingGap();
        } else {
          this.isGreaterThan10years = false;
          this.isFirstRecord = true;
        }
        this.isContentReady = true;

        // Handle query parameters
        this.activatedRoute.queryParams.subscribe(params => {
          const hasMissingIndex = params.missingIndex !== undefined && params.missingIndex !== null;
          const missingIndex = hasMissingIndex ? parseInt(params.missingIndex, 10) : undefined;
          const gapPrevFromDate = params.gapPrevFromDate;
          const gapNextToDate = params.gapNextToDate;

          // If newCurrentAddress is specified, this means we're adding a new current
          // employment to replace an existing one
          if (params.newCurrentAddress) {
            this.isModifyNewCurrentAddress = true;

            // Store the current employment to be updated when saving
            const currentEmployment = this.employmentList.find(emp => emp && emp.isCurrentEmployment);
            if (currentEmployment) {
              this.currentNewEmployment = currentEmployment;
              this.storageService.store('currentEmploymentState', currentEmployment);
            }
          } else {
            this.isModifyNewCurrentAddress = false;
            this.currentNewEmployment = null;
            this.storageService.store('currentEmploymentState', null);
          }
          // If no current employment exists and this isn't a gap fill
          if (
            !params.newCurrentAddress &&
            !this.isFirstRecord &&
            (params.missingIndex === undefined || params.missingIndex === null)
          ) {
            const hasCurrentEmployment = this.employmentList.some(emp => emp && emp.isCurrentEmployment);

            if (!hasCurrentEmployment) {
              // Block adding previous employment without current
              this.toastService.showError('You need to add your current employment first.');
              this.location.back();
              return;
            }
          }

          this.onAddNewEmployment(missingIndex, gapPrevFromDate, gapNextToDate);
        });

        // Handle route parameters for editing existing records
        this.activatedRoute.params.subscribe(params => {
          if (params.id) {
            let empEvent = this.employmentList.find(el => el.id === params.id);
            if (empEvent) {
              this.onUpdateEmploymentPressed(empEvent);
            }
          }
        });
      } else {
        this.location.back();
      }
    });
  }

  private checkMissingGap(): void {
    let isYearsInOrder = true;
    let employmentData: any[] = [...this.employmentList];
    const currentEmployment = this.employmentList.filter(e => e.isCurrentEmployment);
    let currentDifferenceMonth = 0;
    const currentEmpToDate = dayjs(this.currentDate);
    const currentEmpFromDate = dayjs(currentEmployment[0]?.fromDate);
    currentDifferenceMonth = Math.round(currentEmpToDate.diff(currentEmpFromDate, 'month', true));

    if (this.employmentList.length > 1) {
      let spliceIndex = 0;
      for (let index = 0; index < employmentData.length - 1; index++) {
        if (employmentData[index + 1]) {
          const date2CompareToDate = dayjs(employmentData[index + 1].toDate);
          const date2CompareFromDate = dayjs(employmentData[index].fromDate);
          if (date2CompareToDate.diff(date2CompareFromDate, 'month') === 0) {
            const currToDate = dayjs(employmentData[index + 1].toDate);
            const currFromDate = dayjs(employmentData[index + 1].fromDate);

            currentDifferenceMonth += Math.round(currToDate.diff(currFromDate, 'month', true));

            if (this.checkEmploymentHasTenYearsInfo(currentDifferenceMonth, isYearsInOrder)) {
              return;
            }
          } else {
            if (!spliceIndex) {
              spliceIndex = index + 1;
            } else {
              spliceIndex += index;
            }
            if (employmentData.length - 1 === index + 1 && employmentData.length - 2 === index) {
              this.employmentList.splice(this.employmentList.length - 1, 0, { fromDate: null } as Employment);
            } else {
              this.employmentList.splice(spliceIndex, 0, { fromDate: null } as Employment);
            }
            if (index === 0) {
              isYearsInOrder = this.checkIsYearsInOrder(currentDifferenceMonth);
            } else {
              isYearsInOrder = this.checkIsYearsInOrder(currentDifferenceMonth);
            }
          }
        }
      }
    } else {
      this.checkEmploymentHasTenYearsInfo(currentDifferenceMonth, isYearsInOrder);
    }
  }

  private checkEmploymentHasTenYearsInfo(month: number, isYearsInOrder: boolean): boolean {
    if (month >= 120 && isYearsInOrder) {
      this.isGreaterThan10years = true;
      return true;
    } else {
      this.isGreaterThan10years = false;
      return false;
    }
  }

  private checkIsYearsInOrder(currentDiffAddressMonth): boolean {
    if (currentDiffAddressMonth < 120) {
      return false;
    } else {
      return true;
    }
  }
  private getPlaceAutocomplete(addressText: any): void {
    if (!addressText) {
      return;
    }

    const input = addressText?.nativeElement || addressText;
    if (!input) {
      return;
    }

    // Clean up existing autocomplete instance if it exists
    if (input.autocompleteInstance) {
      google.maps.event.clearInstanceListeners(input);
      input.autocompleteInstance = null;
    }

    try {
      const autocomplete = new google.maps.places.Autocomplete(input, {
        componentRestrictions: {
          country: ['US', 'CA']
        },
        types: [],
        fields: ['formatted_address', 'address_components', 'geometry']
      });

      // Set fields explicitly to reduce payload and improve mobile performance
      autocomplete.setFields(['formatted_address', 'address_components', 'geometry']);

      google.maps.event.addListener(autocomplete, 'place_changed', () => {
        const place = autocomplete.getPlace();
        
        // Check if place has required data
        if (place && place.formatted_address) {
          this.zone.run(() => {
            try {
              const formattedAddress = place.formatted_address;
              this.employerAddress.setValue(formattedAddress, { emitEvent: false });
              this.parseAddress(formattedAddress);
              const provinceMatch = formattedAddress.match(/, ([A-Z]{2}) \w+ \d{5}|\d{3}|\d{4},/);
              let province = provinceMatch ? provinceMatch[1] : null;

              const countryMatch = formattedAddress.match(/, ([A-Za-z\s]+)$/);
              const country = countryMatch ? countryMatch[1].trim() : null;
              this.employerCountry = this.getCountryCodeByKey(country);
              if (country === 'Canada') {
                this.assignedRadiusValues = this.radiusCanada;
              } else if (country === 'USA') {
                this.assignedRadiusValues = this.radiusUs;
              } else {
                this.assignedRadiusValues = [];
              }
              this.assignedRadius.reset();

              if (!province) {
                const fullAddress = formattedAddress.split(',');
                if (fullAddress.length > 2) {
                  const provinceAndZip = fullAddress[fullAddress.length - 2].trim();
                  const getProvince = provinceAndZip.split(' ');
                  if (getProvince.length > 0) {
                    province = getProvince[0];
                  }
                }
              }
              //  this.setLicense(province);
            } catch (error) {
              console.error('Error processing place selection:', error);
            }
          });
        }
      });

      // Mobile-specific: Ensure autocomplete works well on mobile
      // Note: Removed focus event trigger to prevent Google Maps API errors

      // Store autocomplete instance in the element for later removal
      input.autocompleteInstance = autocomplete;
    } catch (error) {
      console.error('Error initializing Google Places Autocomplete:', error);
    }
  }
  parseAddress(addressTxt: string) {
    const parsedAddressArr = addressTxt?.split(',');
    this.employerAddress.setValue(addressTxt);
    this.form.get('streetAddress').setValue(parsedAddressArr[0]?.trim());
    this.form.get('city').setValue(parsedAddressArr[1]?.trim());
    let getCountry = addressTxt.includes('Canada') ? 'CA' : addressTxt.includes('USA') ? 'US' : null;
    let state = parsedAddressArr[2]?.trim();
    
    state = state.split(' ')[0];
    let country = getCountry ? getCountry : parsedAddressArr[3]?.trim();
    parsedAddressArr[3]?.trim();
    if (country == 'Canada' || country == 'CA') {
      country = 'CA';
    }
    if (country == 'USA' || country == 'US') {
      country = 'US';
      state = `US-${state}`;
    }
    this.form.get('zipCode').setValue(state.substring(3, state?.length)?.trim());
    this.form.get('province').setValue(state);
    this.form.get('country').setValue(country);
  }
  private loadGoogleAutoComplete(): void {
    this.getPlaceAutocomplete(this.empAddressText);
  }
  onGoogleAddressLookupToggle(event: any): void {
    // Toggle the Google Address Lookup state
    this.isGoogleAddressLookupEnabled = !this.isGoogleAddressLookupEnabled;

    if (!this.isGoogleAddressLookupEnabled) {
      // Disable Google Address Lookup and full address field
      this.form.get('employerAddress').clearValidators();
      this.form.get('employerAddress').updateValueAndValidity();

      // Allow manual entry by clearing the field or enabling it
      this.form.get('streetAddress').setValue('');
      this.form.get('country').setValue('');
      this.form.get('province').setValue('');
      this.form.get('city').setValue('');
      this.form.get('zipCode').setValue('');

      // Enable separate address field validations
      this.form.get('streetAddress').setValidators([Validators.required]);
      this.form.get('city').setValidators([Validators.required]);
      this.form.get('province').setValidators([Validators.required]);
      this.form.get('zipCode').setValidators([Validators.required]);
      this.form.get('country').setValidators([Validators.required]);

      const input = this.empAddressText.nativeElement; // Check if addressText is available
      if (input?.autocompleteInstance) {
        google.maps.event.clearInstanceListeners(input); // Remove listeners and instance
        delete input.autocompleteInstance;
      }
    } else {
      // Enable Google Address Lookup and full address field
      this.form
        .get('employerAddress')
        .setValidators([
          Validators.required,
          Validators.maxLength(100),
          Validators.pattern(
            "^[/#a-zA-Z0-9-'. àâçéèêëîïôûùüÿñ]*,[/#a-zA-Z0-9-'. àâçéèêëîïôûùüÿñ]*, [A-Z]{2} [A-Z0-9 ]*, [A-Za-z]+$"
          )
        ]);
      // Clear separate address field validations
      ['streetAddress', 'city', 'province', 'zipCode', 'country'].forEach(field => {
        this.form.get(field).clearValidators();
        this.form.get(field).updateValueAndValidity();
      });

      // Clear address field to avoid default commas
      this.form.get('employerAddress').setValue('');

      if (this.empAddressText?.nativeElement) {
        this.getPlaceAutocomplete(this.empAddressText);
      } else {
        this.isGoogleLookupReinitPending = true; // Schedule reinitialization if input is not available
      }
    }
  }
  onCountryChange(): void {
    this.selectedCountry = this.form.get('country')?.value;
    this.employerCountry = this.selectedCountry;
    this.setProvinces();
    if (this.selectedCountry === 'CA') {
      this.assignedRadiusValues = this.radiusCanada;
    } else if (this.selectedCountry === 'US') {
      this.assignedRadiusValues = this.radiusUs;
    } else {
      this.assignedRadiusValues = [];
    }
    this.form.get('assignedRadius')?.reset();
    this.form.get('licenseType')?.reset();
    if (!this.isUSOrCanada(this.selectedCountry)) {
      this.form.get('position')?.setValue('other');
    } else {
      this.form.get('position')?.reset();
    }
  }
  setProvinces(): void {
    if (this.selectedCountry === 'CA') {
      this.provinces = this.canadaProvinces;
    } else if (this.selectedCountry === 'US') {
      this.provinces = this.usaProvinces;
    } else {
      this.provinces = [];
    }
    // Reset province selection only when changing country
    this.form.get('province')?.reset();
  }
  onProvinceChange(): void {
    const selectedProvince = this.form.get('province')?.value;
    // Set license class based on province selection in Canada
    // if (this.selectedCountry === 'CA') {
    //   if (selectedProvince === 'ON') {
    //     this.provinceLicenseClass = this.licenseClassON;
    //   } else {
    //     this.provinceLicenseClass = this.licenseClassNonON;
    //   }
    // } else if (this.selectedCountry === 'US') {
    //   this.provinceLicenseClass = this.licenseClassNonON;
    // } else {
    //   this.provinceLicenseClass = [];
    // }
    this.provinceLicenseClass = this.allLicenseClasses;
    // Reset the license type field only when the province changes
    this.form.get('licenseType')?.reset();
  }
  shouldShowPositionField(): boolean {
    const country = this.form.get('country')?.value;
    const employerAddress = this.form.get('employerAddress')?.value || '';

    if (!country) {
      return false;
    }

    return (
      country === 'Canada' ||
      country === 'CA' ||
      country === 'USA' ||
      country === 'US' ||
      employerAddress.includes('US') ||
      employerAddress.includes('CA') ||
      employerAddress.includes('Canada') ||
      !this.isUSOrCanada(country)
    );
  }

  isUSOrCanada(country: string): boolean {
    return ['USA', 'US', 'Canada', 'CA'].includes(country);
  }

  // Helper method to convert month/year to date
  private updateDateFieldsFromMonthYear(): void {
    const fromMonth = this.form.get('fromMonth')?.value;
    const fromYear = this.form.get('fromYear')?.value;
    const toMonth = this.form.get('toMonth')?.value;
    const toYear = this.form.get('toYear')?.value;


    // Update FROM date - use start of month (day 1)
    // Only update if BOTH month and year are set
    if ((fromMonth === 0 || fromMonth) && fromMonth !== null && fromMonth !== '' && fromYear) {
      const fromDate = new Date(fromYear, fromMonth, 1); // Day 1 of the selected month
      this.form.patchValue({ fromDate: fromDate }, { emitEvent: false });
    } else {
    }

    // Update TO date - use end of month (last day)
    // Only update if BOTH month and year are set
    if ((toMonth === 0 || toMonth) && toMonth !== null && toMonth !== '' && toYear) {
      const toDate = new Date(toYear, toMonth + 1, 0); // Last day of the selected month
      this.form.patchValue({ toDate: toDate }, { emitEvent: false });
    } else {
    }

    this.form.get('toDate')?.updateValueAndValidity({ emitEvent: false });
  }

  // Helper method to populate month/year dropdowns from existing date
  private populateMonthYearFromDate(employment: Employment): void {
    
    if (employment.fromDate) {
      const fromDate = dayjs(employment.fromDate);
      this.form.patchValue({
        fromMonth: fromDate.month(),
        fromYear: fromDate.year()
      });
    }

    if (employment.isCurrentEmployment) {
      // For current employment, always use current month/year for TO date
      const currentMonth = dayjs(this.currentDate).month();
      const currentYear = dayjs(this.currentDate).year();
      this.form.patchValue({
        toMonth: currentMonth,
        toYear: currentYear
      });
      // Clear validators for current employment
      this.form.get('toMonth')?.clearValidators();
      this.form.get('toYear')?.clearValidators();
      this.form.get('toMonth')?.updateValueAndValidity();
      this.form.get('toYear')?.updateValueAndValidity();
    } else if (employment.toDate) {
      const toDate = dayjs(employment.toDate);
      this.form.patchValue({
        toMonth: toDate.month(),
        toYear: toDate.year()
      });
    }

    
  }

  // Update all cached dropdown options
  private updateDropdownOptions(): void {
    this.availableFromYears = this.getAvailableFromYears();
    this.availableFromMonths = this.getAvailableFromMonths();
    this.availableToYears = this.getAvailableToYears();
    this.availableToMonths = this.getAvailableToMonths();
  }

  // Get available years for FROM year dropdown
  private getAvailableFromYears(): number[] {
    const isCurrentEmployment = this.form.get('isCurrentEmployment')?.value;
    const editingEmploymentId = this.form.get('id')?.value;
    
    let availableYears = this.years;

    // Current employment: allow any year (concurrent periods supported; no "must start after last job" rule)
    if (isCurrentEmployment) {
      return availableYears;
    }
    
    // If editing an existing record, allow all years (don't restrict)
    if (editingEmploymentId) {
      return availableYears;
    }

    // For gap filling, use minFromDate and maxToDate constraints instead of filtering by all employments
    if (this.minFromDate && this.maxToDate) {
      const minYear = dayjs(this.minFromDate).year();
      const maxYear = dayjs(this.maxToDate).year();
      availableYears = availableYears.filter(year => year >= minYear && year <= maxYear);
      return availableYears;
    }

    return availableYears;
  }

  // Get available months for FROM month dropdown
  private getAvailableFromMonths(): any[] {
    return this.months;
  }

  // Get available years for TO year dropdown
  private getAvailableToYears(): number[] {
    const isCurrentEmployment = this.form.get('isCurrentEmployment')?.value;
    const fromYear = this.form.get('fromYear')?.value;
    const editingEmploymentId = this.form.get('id')?.value;

    let availableYears = this.years;
    const todayYear = new Date().getFullYear();

    // For current employment, TO year is always current year (and disabled)
    if (isCurrentEmployment) {
      return [dayjs(this.currentDate).year()];
    }

    // If editing an existing record, only filter by fromYear (allow all years >= fromYear)
    if (editingEmploymentId && fromYear) {
      return availableYears.filter(year => year >= fromYear && year <= todayYear);
    }

    // TO year must be >= FROM year
    if (fromYear) {
      availableYears = availableYears.filter(year => year >= fromYear);
    }

    availableYears = availableYears.filter(year => year <= todayYear);

    // For gap filling, use minFromDate and maxToDate constraints
    if (this.minFromDate && this.maxToDate) {
      const minYear = fromYear || dayjs(this.minFromDate).year();
      const maxYear = Math.min(dayjs(this.maxToDate).year(), todayYear);
      availableYears = availableYears.filter(year => year >= minYear && year <= maxYear);
      return availableYears;
    }

    return availableYears;
  }

  // Get available months for TO month dropdown
  private getAvailableToMonths(): any[] {
    const isCurrentEmployment = this.form.get('isCurrentEmployment')?.value;
    const fromMonth = this.form.get('fromMonth')?.value;
    const fromYear = this.form.get('fromYear')?.value;
    const toYear = this.form.get('toYear')?.value;

    // For current employment, TO month is current month (and disabled)
    if (isCurrentEmployment) {
      return [this.months[dayjs(this.currentDate).month()]];
    }

    let availableMonths = [...this.months];

    // TO month must be >= FROM month if same year
    if (fromYear && toYear && fromYear === toYear && (fromMonth === 0 || fromMonth)) {
      availableMonths = availableMonths.filter(month => month.value >= fromMonth);
    }

    const todayY = new Date().getFullYear();
    const todayM = new Date().getMonth();
    if (toYear === todayY) {
      availableMonths = availableMonths.filter(month => month.value <= todayM);
    }

    return availableMonths;
  }

  // Get the current month name
  getCurrentMonthName(): string {
    const currentMonth = dayjs(this.currentDate).month();
    return this.months[currentMonth]?.name || '';
  }

  // Get the current year
  getCurrentYear(): number {
    return dayjs(this.currentDate).year();
  }
}
